import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import './AdminDashboardPage.css';

function StatCard({ label, value, sub, accent }) {
  return (
    <div className={`stat-card${accent ? ' stat-card--accent' : ''}`}>
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value}</div>
      {sub && <div className="stat-card__sub">{sub}</div>}
    </div>
  );
}

const STATUS_BADGE_MAP = {
  'New': 'new', 'Under Review': 'review', 'Shortlisted': 'shortlisted',
  'Selected': 'selected', 'Not Selected': 'rejected',
  'Discovery Meeting Scheduled': 'meeting', 'Converted to Paid Lead': 'converted', 'Closed': 'closed',
};

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getDashboard()
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async () => {
    try {
      const res = await adminApi.exportApplications({ format: 'xlsx' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'adeona_applications.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Export failed. Please try again.'); }
  };

  if (loading) return <div className="page-loading"><div className="spinner" /><span>Loading dashboard...</span></div>;
  if (!data) return <div className="page-loading">Failed to load dashboard.</div>;

  const selected = data.byStatus.find(s => s.status === 'Selected')?.count || 0;
  const slotsLeft = (data.maxSelected || 10) - parseInt(selected);

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1>Dashboard</h1>
          <p className="text-muted text-small">SME Digital Empowerment Initiative 2026</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn--outline" onClick={handleExport}>Export Excel</button>
          <Link to="/admin/applications" className="btn btn--primary">View All →</Link>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <StatCard label="Total Applications" value={data.total} sub="received so far" />
        <StatCard label="Shortlisted" value={data.shortlisted} sub={`${selected} selected`} />
        <StatCard label="Consultation Requests" value={data.consultationRequests} sub="warm leads pipeline" />
        <StatCard label="Slots Remaining" value={slotsLeft < 0 ? 0 : slotsLeft} sub={`of ${data.maxSelected} total`} accent={slotsLeft <= 3} />
      </div>

      <div className="dashboard__grid">
        {/* Status breakdown */}
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 14 }}>Applications by Status</h3>
          {data.byStatus.map(s => (
            <div key={s.status} className="stat-row">
              <span className={`badge badge--${STATUS_BADGE_MAP[s.status] || 'new'}`}>{s.status}</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{s.count}</span>
            </div>
          ))}
        </div>

        {/* Industry breakdown */}
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 14 }}>Top Industries</h3>
          {data.byIndustry.slice(0, 8).map(s => (
            <div key={s.industry} className="stat-row">
              <span style={{ fontSize: 13, color: 'var(--text2)' }}>{s.industry}</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{s.count}</span>
            </div>
          ))}
        </div>

        {/* District breakdown */}
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 14 }}>Top Districts</h3>
          {data.byDistrict.slice(0, 8).map(s => (
            <div key={s.district} className="stat-row">
              <span style={{ fontSize: 13, color: 'var(--text2)' }}>{s.district}</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{s.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
